import { useState, useEffect, useRef } from "react";
import toast from "../../../utils/toast";
import {
  getCommunicationSettingsSmtp,
  getCommunicationSettingsSmtpById,
  insertUpdateCommunicationSettingsSmtp,
  deleteCommunicationSettingsSmtpById,
  toggleCommunicationSettingsSmtpById,
  getNotificationTemplateEmail,
  getNotificationTemplateEvent,
  getNotificationTemplateEventVariable,
  getNotificationTemplateById,
  insertUpdateNotificationTemplate,
  deleteNotificationTemplate,
  toggleNotificationTemplate,
} from "../../../services/propertyOwner/propertyOwnerServices";

const INITIAL_ADD_FORM = {
  label: "",
  smtpHostValue: "",
  port: "",
  username: "",
  password: "",
  fromName: "",
  fromEmail: "",
  encryptionType: "TLS",
  isActive: false,
};

export default function OwnerEmailSetting() {
  const [smtpList, setSmtpList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editFetching, setEditFetching] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  const [templateEvents, setTemplateEvents] = useState([]);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false);

  // Add Modal Template
  const [addTplOpen, setAddTplOpen] = useState(false);
  const [addTplForm, setAddTplForm] = useState({
    eventId: "",
    templateName: "",
    subject: "",
    bodyText: "",
    isHtml: true,
    isActive: true,
    sortOrder: 0,
    extraFieldsJson: "",
  });
  const [addTplErrors, setAddTplErrors] = useState({});
  const [addTplLoading, setAddTplLoading] = useState(false);
  const bodyEditorAddRef = useRef(null);

  // Edit Modal Template
  const [editTplOpen, setEditTplOpen] = useState(false);
  const [editTplId, setEditTplId] = useState(null);
  const [editTplForm, setEditTplForm] = useState({});
  const [editTplErrors, setEditTplErrors] = useState({});
  const [editTplLoading, setEditTplLoading] = useState(false);
  const [editTplFetching, setEditTplFetching] = useState(false);
  const bodyEditorEditRef = useRef(null);
  const [previewTpl, setPreviewTpl] = useState(null);

  useEffect(() => {
    fetchList();
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    if (
      !editTplFetching &&
      editTplOpen &&
      bodyEditorEditRef.current &&
      editTplForm.bodyText
    ) {
      bodyEditorEditRef.current.innerHTML = editTplForm.bodyText;
    }
  }, [editTplFetching]);

  const fetchList = async () => {
    setListLoading(true);
    try {
      const res = await getCommunicationSettingsSmtp();
      if (res.statusCode === 1) setSmtpList(res.data || []);
    } catch {
      toast.error("Failed to load SMTP configurations");
    } finally {
      setListLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await getNotificationTemplateEmail();
      if (res.statusCode === 1) setEmailTemplates(res.data || []);
    } catch {
      console.log("Failed to load email templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  // ── Add ──
  const openAddModal = () => {
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setAddOpen(true);
  };

  const validateAdd = () => {
    const e = {};
    if (!addForm.label.trim()) e.label = "Required";
    if (!addForm.smtpHostValue.trim()) e.smtpHostValue = "Required";
    if (!addForm.port) e.port = "Required";
    if (!addForm.username.trim()) e.username = "Required";
    if (!addForm.password.trim()) e.password = "Required";
    if (!addForm.fromName.trim()) e.fromName = "Required";
    if (!addForm.fromEmail.trim()) e.fromEmail = "Required";
    return e;
  };

  const handleAdd = async () => {
    const errors = validateAdd();
    if (Object.keys(errors).length) {
      setAddErrors(errors);
      return;
    }
    setAddLoading(true);
    try {
      const res = await insertUpdateCommunicationSettingsSmtp({ ...addForm });
      if (res.statusCode === 1) {
        toast.success(res.message);
        setAddOpen(false);
        fetchList();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to add SMTP");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit ──
  const openEditModal = async (id) => {
    setEditId(id);
    setEditErrors({});
    setEditFetching(true);
    setEditOpen(true);
    try {
      const res = await getCommunicationSettingsSmtpById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditForm({
          label: d.label || "",
          smtpHostValue: d.smtpHostValue || "",
          port: d.port || "",
          username: d.username || "",
          password: d.password || "",
          fromName: d.fromName || "",
          fromEmail: d.fromEmail || "",
          encryptionType: d.encryptionType || "TLS",
          isActive: d.isActive || false,
        });
      } else {
        toast.error(res.message);
        setEditOpen(false);
      }
    } catch {
      toast.error("Failed to load SMTP config");
      setEditOpen(false);
    } finally {
      setEditFetching(false);
    }
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.label.trim()) e.label = "Required";
    if (!editForm.smtpHostValue.trim()) e.smtpHostValue = "Required";
    if (!editForm.port) e.port = "Required";
    if (!editForm.username.trim()) e.username = "Required";
    if (!editForm.fromName.trim()) e.fromName = "Required";
    if (!editForm.fromEmail.trim()) e.fromEmail = "Required";
    return e;
  };

  const handleEdit = async () => {
    const errors = validateEdit();
    if (Object.keys(errors).length) {
      setEditErrors(errors);
      return;
    }
    setEditLoading(true);
    try {
      const payload = { ...editForm, id: editId };

      const res = await insertUpdateCommunicationSettingsSmtp(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setEditOpen(false);
        fetchList();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      const message =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";

      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteCommunicationSettingsSmtpById(deleteId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setDeleteId(null);
        fetchList();
      } else {
        toast.error(res.message);
      }
    } catch {
      console.log("Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Toggle ──
  const handleToggle = async (id) => {
    setTogglingId(id);
    try {
      const res = await toggleCommunicationSettingsSmtpById(id);

      if (res.statusCode === 1) {
        toast.success(res.message);

        setSmtpList((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, isActive: !item.isActive } : item,
          ),
        );
      } else {
        toast.error(res.message);
      }
    } catch {
      console.log("Failed to toggle");
    } finally {
      setTogglingId(null);
    }
  };

  const fetchTemplateEvents = async () => {
    try {
      const res = await getNotificationTemplateEvent();
      if (res.statusCode === 1) setTemplateEvents(res.data || []);
    } catch {
      console.log("Failed to load events");
    }
  };

  // fetch variables when event changes
  const fetchVariables = async (eventId) => {
    if (!eventId) {
      setTemplateVariables([]);
      return;
    }
    try {
      const res = await getNotificationTemplateEventVariable(eventId);
      if (res.statusCode === 1) setTemplateVariables(res.data || []);
    } catch {
      console.log("Failed to load variables");
    }
  };

  // insert variable into contenteditable
  const insertVariable = (placeholder, ref) => {
    const editor = ref.current;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    if (sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(placeholder));
      range.collapse(false);
    } else {
      editor.innerHTML += placeholder;
    }
  };

  // ── Add Template ──
  const openAddTplModal = () => {
    setAddTplForm({
      eventId: "",
      templateName: "",
      subject: "",
      bodyText: "",
      isHtml: true,
      isActive: true,
      sortOrder: 0,
      extraFieldsJson: "",
    });
    setAddTplErrors({});
    setTemplateVariables([]);
    fetchTemplateEvents();
    setAddTplOpen(true);
    setTimeout(() => {
      if (bodyEditorAddRef.current) bodyEditorAddRef.current.innerHTML = "";
    }, 0);
  };

  const handleAddTplEventChange = (eventId) => {
    const selectedEvent = templateEvents.find(
      (ev) => ev.eventId === Number(eventId),
    );
    setAddTplForm((p) => ({
      ...p,
      eventId,
      templateName: selectedEvent ? selectedEvent.eventName : "",
    }));
    setAddTplErrors((p) => ({ ...p, eventId: "" }));
    fetchVariables(eventId);
  };

  const handleAddTplSubmit = async () => {
    const e = {};
    if (!addTplForm.eventId) e.eventId = "Required";
    if (!addTplForm.subject.trim()) e.subject = "Required";
    const body = bodyEditorAddRef.current?.innerHTML || "";
    if (!body.trim()) e.bodyText = "Required";
    if (Object.keys(e).length) {
      setAddTplErrors(e);
      return;
    }
    setAddTplLoading(true);
    try {
      const payload = {
        id: 0,
        eventId: Number(addTplForm.eventId),
        channel: "Email",
        templateName: addTplForm.templateName,
        subject: addTplForm.subject,
        bodyText: body,
        isHtml: true,
        isActive: addTplForm.isActive,
        sortOrder: Number(addTplForm.sortOrder),
        extraFieldsJson: "",
      };
      const res = await insertUpdateNotificationTemplate(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setAddTplOpen(false);
        fetchEmailTemplates();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to create template");
    } finally {
      setAddTplLoading(false);
    }
  };

  // ── Edit Template ──
  const openEditTplModal = async (id) => {
    setEditTplId(id);
    setEditTplErrors({});
    setEditTplFetching(true);
    setEditTplOpen(true);
    fetchTemplateEvents();
    try {
      const res = await getNotificationTemplateById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditTplForm({
          eventId: d.eventId || "",
          templateName: d.templateName || "",
          subject: d.subject || "",
          bodyText: d.bodyText || "",
          isHtml: d.isHtml,
          isActive: d.isActive,
          sortOrder: d.sortOrder || 0,
          extraFieldsJson: "",
        });
        fetchVariables(d.eventId);
      } else {
        toast.error(res.message);
        setEditTplOpen(false);
      }
    } catch {
      toast.error("Failed to load template");
      setEditTplOpen(false);
    } finally {
      setEditTplFetching(false);
    }
  };

  const handleEditTplEventChange = (eventId) => {
    const selectedEvent = templateEvents.find(
      (ev) => ev.eventId === Number(eventId),
    );
    setEditTplForm((p) => ({
      ...p,
      eventId,
      templateName: selectedEvent ? selectedEvent.eventName : p.templateName,
    }));
    fetchVariables(eventId);
  };

  const handleEditTplSubmit = async () => {
    const e = {};
    if (!editTplForm.eventId) e.eventId = "Required";

    if (!editTplForm.subject.trim()) e.subject = "Required";
    const body = bodyEditorEditRef.current?.innerHTML || "";
    if (!body.trim()) e.bodyText = "Required";
    if (Object.keys(e).length) {
      setEditTplErrors(e);
      return;
    }
    setEditTplLoading(true);
    try {
      const payload = {
        id: editTplId,
        eventId: Number(editTplForm.eventId),
        channel: "Email",
        templateName: editTplForm.templateName,
        subject: editTplForm.subject,
        bodyText: body,
        isHtml: true,
        isActive: editTplForm.isActive,
        sortOrder: Number(editTplForm.sortOrder),
        extraFieldsJson: "",
      };
      const res = await insertUpdateNotificationTemplate(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setEditTplOpen(false);
        fetchEmailTemplates();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to update template");
    } finally {
      setEditTplLoading(false);
    }
  };

  // ── Delete Template ──
  const handleDeleteTemplate = async () => {
    setDeleteTemplateLoading(true);
    try {
      const res = await deleteNotificationTemplate(deleteTemplateId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setDeleteTemplateId(null);
        fetchEmailTemplates();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to delete template");
    } finally {
      setDeleteTemplateLoading(false);
    }
  };

  // ── Toggle Template ──
  const handleToggleTemplate = async (id) => {
    try {
      const res = await toggleNotificationTemplate(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setEmailTemplates((prev) =>
          prev.map((t) =>
            t.templateId === id ? { ...t, isActive: !t.isActive } : t,
          ),
        );
      } else {
        toast.error(res.message);
      }
    } catch {
      console.log("Failed to toggle template");
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Email Setting
            </div>
            <div className="page-sub" id="page-sub">
              API Settings › Email / SMTP Configuration
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>

        <div className="content" id="content-area">
          <div className="card mb16">
            <div className="flex-between mb12">
              <div className="card-title" style={{ margin: 0 }}>
                SMTP Configurations
              </div>
              <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                + Add SMTP
              </button>
            </div>
            <div className="info-box mb12">
              Add multiple SMTP configs. Only one can be active at a time — it
              will be used for all outgoing emails.
            </div>

            {listLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    border: "0.5px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    padding: "14px 16px",
                    marginBottom: 12,
                  }}
                >
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div
                      key={j}
                      style={{
                        height: 14,
                        borderRadius: 6,
                        background:
                          "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        width: j === 0 ? "40%" : "70%",
                        marginBottom: 8,
                      }}
                    />
                  ))}
                </div>
              ))
            ) : smtpList.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 0",
                  color: "var(--text-secondary)",
                  fontSize: 13,
                }}
              >
                No SMTP configurations added yet.
              </div>
            ) : (
              smtpList.map((item) => (
                <div
                  key={item.id}
                  style={{
                    border: "0.5px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    padding: "14px 16px",
                    marginBottom: 12,
                    opacity: togglingId === item.id ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={item.isActive}
                          disabled={togglingId === item.id}
                          onChange={() => handleToggle(item.id)}
                        />
                        <span className="toggle-slider" />
                      </label>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {item.label}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {item.smtpHostValue}:{item.port}
                        </div>
                      </div>
                      {item.isActive && (
                        <span
                          className="badge badge-active"
                          style={{ fontSize: 10 }}
                        >
                          Active
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="btn btn-xs"
                        onClick={() => openEditModal(item.id)}
                      >
                        ✎ Edit
                      </button>
                      <button
                        className="btn btn-xs btn-danger"
                        onClick={() => setDeleteId(item.id)}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 6,
                      fontSize: 11,
                      color: "var(--text-secondary)",
                    }}
                  >
                    <span>👤 {item.username}</span>
                    <span>🔒 {item.encryptionType}</span>
                    <span>📛 From: {item.fromName}</span>
                    <span>📧 {item.fromEmail}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="card">
            <div
              className="flex-between mb12"
              style={{ flexWrap: "wrap", gap: 10 }}
            >
              <div>
                <div className="card-title" style={{ margin: 0 }}>
                  📧 Email Templates
                  <span className="badge badge-info" style={{ marginLeft: 6 }}>
                    {emailTemplates.length}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    marginTop: 2,
                  }}
                >
                  Reusable Email message bodies. Insert variables like
                  <code
                    style={{
                      fontFamily: "monospace",
                      background: "var(--bg-secondary)",
                      padding: "1px 4px",
                      borderRadius: 3,
                    }}
                  >
                    {"{{tenant_name}}"}
                  </code>{" "}
                  for personalisation.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="🔍 Search email templates"
                  style={{ fontSize: 12, padding: "6px 10px", width: 200 }}
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={openAddTplModal}
                >
                  + Create Template
                </button>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 200 }}>Name</th>
                    <th>Subject</th>
                    <th>Body Preview</th>
                    <th style={{ width: 140 }}>Last Modified</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templatesLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : emailTemplates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No email templates found.
                      </td>
                    </tr>
                  ) : (
                    emailTemplates.map((t) => (
                      <tr key={t.templateId}>
                        <td style={{ fontWeight: 600 }}>{t.templateName}</td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {t.subject}
                        </td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                            maxWidth: 200,
                          }}
                        >
                          {t.bodyText
                            ?.replace(/<[^>]*>/g, " ")
                            .substring(0, 80)}
                          …
                        </td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-tertiary)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.createdAt
                            ? new Date(t.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              onClick={() => setPreviewTpl(t)}
                            >
                              <span className="tip">Preview</span>👁
                            </span>
                            <span
                              className="action-icon"
                              onClick={() => openEditTplModal(t.templateId)}
                            >
                              <span className="tip">Edit</span>✎
                            </span>
                            <span
                              className="action-icon red"
                              onClick={() => setDeleteTemplateId(t.templateId)}
                            >
                              <span className="tip">Delete</span>🗑
                            </span>
                            <span
                              className="btn btn-xs"
                              title={t.isActive ? "Activate" : "Deactivate"}
                              style={{
                                background: t.isActive
                                  ? "var(--green-light, #f5bbbb)"
                                  : "var(--bg-secondary)",
                                color: t.isActive
                                  ? "var(--green, #16a34a)"
                                  : "var(--text-secondary)",
                                border: `1px solid ${t.isActive ? "var(--green, #16a34a)" : "var(--red, #e65858)"}`,
                              }}
                              onClick={() => handleToggleTemplate(t.templateId)}
                              disabled={togglingId === t.id}
                            >
                              {t.isActive ? "✓" : "⊗"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      {addOpen && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 580, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Add SMTP Configuration</div>
              <button className="modal-close" onClick={() => setAddOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Label / Name *</label>
                <input
                  type="text"
                  value={addForm.label}
                  placeholder="e.g. Gmail Primary, SendGrid"
                  onChange={(e) => {
                    setAddForm((p) => ({ ...p, label: e.target.value }));
                    setAddErrors((p) => ({ ...p, label: "" }));
                  }}
                  style={addErrors.label ? { borderColor: "#dc2626" } : {}}
                />
                {addErrors.label && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addErrors.label}
                  </span>
                )}
              </div>
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>SMTP Host *</label>
                  <input
                    type="text"
                    value={addForm.smtpHostValue}
                    placeholder="smtp.gmail.com"
                    onChange={(e) => {
                      setAddForm((p) => ({
                        ...p,
                        smtpHostValue: e.target.value,
                      }));
                      setAddErrors((p) => ({ ...p, smtpHostValue: "" }));
                    }}
                    style={
                      addErrors.smtpHostValue ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {addErrors.smtpHostValue && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.smtpHostValue}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Port *</label>
                  <input
                    type="number"
                    value={addForm.port}
                    placeholder="Port Value"
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, port: e.target.value }));
                      setAddErrors((p) => ({ ...p, port: "" }));
                    }}
                    style={addErrors.port ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.port && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.port}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={addForm.username}
                    placeholder="your@email.com"
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, username: e.target.value }));
                      setAddErrors((p) => ({ ...p, username: "" }));
                    }}
                    style={addErrors.username ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.username && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.username}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="text"
                    value={addForm.password}
                    placeholder="SMTP password or app key"
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, password: e.target.value }));
                      setAddErrors((p) => ({ ...p, password: "" }));
                    }}
                    style={addErrors.password ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.password && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.password}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>From Name *</label>
                  <input
                    type="text"
                    value={addForm.fromName}
                    placeholder="NestManager Platform"
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, fromName: e.target.value }));
                      setAddErrors((p) => ({ ...p, fromName: "" }));
                    }}
                    style={addErrors.fromName ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.fromName && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.fromName}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>From Email *</label>
                  <input
                    type="email"
                    value={addForm.fromEmail}
                    placeholder="noreply@domain.com"
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, fromEmail: e.target.value }));
                      setAddErrors((p) => ({ ...p, fromEmail: "" }));
                    }}
                    style={
                      addErrors.fromEmail ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {addErrors.fromEmail && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.fromEmail}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>Encryption</label>
                  <select
                    value={addForm.encryptionType}
                    onChange={(e) =>
                      setAddForm((p) => ({
                        ...p,
                        encryptionType: e.target.value,
                      }))
                    }
                  >
                    <option value="TLS">TLS</option>
                    <option value="SSL">SSL</option>
                    <option value="None">None</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={addForm.isActive}
                        onChange={(e) =>
                          setAddForm((p) => ({
                            ...p,
                            isActive: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-slider" />
                    </label>
                    <span style={{ fontSize: 12 }}>Set as Active SMTP</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setAddOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAdd}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "Save SMTP"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editOpen && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 580, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Edit SMTP Configuration</div>
              <button
                className="modal-close"
                onClick={() => setEditOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {editFetching ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px 0",
                    color: "var(--text-secondary)",
                    fontSize: 13,
                  }}
                >
                  Loading...
                </div>
              ) : (
                <>
                  <div className="form-group mb12">
                    <label>Label / Name *</label>
                    <input
                      type="text"
                      value={editForm.label}
                      onChange={(e) => {
                        setEditForm((p) => ({
                          ...p,
                          label: e.target.value,
                        }));
                        setEditErrors((p) => ({ ...p, label: "" }));
                      }}
                      style={editErrors.label ? { borderColor: "#dc2626" } : {}}
                    />
                    {editErrors.label && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editErrors.label}
                      </span>
                    )}
                  </div>
                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>SMTP Host *</label>
                      <input
                        type="text"
                        value={editForm.smtpHostValue}
                        onChange={(e) => {
                          setEditForm((p) => ({
                            ...p,
                            smtpHostValue: e.target.value,
                          }));
                          setEditErrors((p) => ({ ...p, smtpHostValue: "" }));
                        }}
                        style={
                          editErrors.smtpHostValue
                            ? { borderColor: "#dc2626" }
                            : {}
                        }
                      />
                      {editErrors.smtpHostValue && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editErrors.smtpHostValue}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Port *</label>
                      <input
                        type="number"
                        value={editForm.port}
                        onChange={(e) => {
                          setEditForm((p) => ({
                            ...p,
                            port: e.target.value,
                          }));
                          setEditErrors((p) => ({ ...p, port: "" }));
                        }}
                        style={
                          editErrors.port ? { borderColor: "#dc2626" } : {}
                        }
                      />
                      {editErrors.port && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editErrors.port}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>Username *</label>
                      <input
                        type="text"
                        value={editForm.username}
                        onChange={(e) => {
                          setEditForm((p) => ({
                            ...p,
                            username: e.target.value,
                          }));
                          setEditErrors((p) => ({ ...p, username: "" }));
                        }}
                        style={
                          editErrors.username ? { borderColor: "#dc2626" } : {}
                        }
                      />
                      {editErrors.username && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editErrors.username}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Password (leave blank to keep current)</label>
                      {console.log(editForm)}
                      <input
                        type="text"
                        value={editForm.password}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            password: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>From Name *</label>
                      <input
                        type="text"
                        value={editForm.fromName}
                        onChange={(e) => {
                          setEditForm((p) => ({
                            ...p,
                            fromName: e.target.value,
                          }));
                          setEditErrors((p) => ({ ...p, fromName: "" }));
                        }}
                        style={
                          editErrors.fromName ? { borderColor: "#dc2626" } : {}
                        }
                      />
                      {editErrors.fromName && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editErrors.fromName}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>From Email *</label>
                      <input
                        type="email"
                        value={editForm.fromEmail}
                        onChange={(e) => {
                          setEditForm((p) => ({
                            ...p,
                            fromEmail: e.target.value,
                          }));
                          setEditErrors((p) => ({ ...p, fromEmail: "" }));
                        }}
                        style={
                          editErrors.fromEmail ? { borderColor: "#dc2626" } : {}
                        }
                      />
                      {editErrors.fromEmail && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editErrors.fromEmail}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>Encryption</label>
                      <select
                        value={editForm.encryptionType}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            encryptionType: e.target.value,
                          }))
                        }
                      >
                        <option value="TLS">TLS</option>
                        <option value="SSL">SSL</option>
                        <option value="None">None</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 8,
                        }}
                      >
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={editForm.isActive}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                isActive: e.target.checked,
                              }))
                            }
                          />
                          <span className="toggle-slider" />
                        </label>
                        <span style={{ fontSize: 12 }}>Set as Active SMTP</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {!editFetching && (
              <div className="modal-footer">
                <button className="btn" onClick={() => setEditOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleEdit}
                  disabled={editLoading}
                >
                  {editLoading ? "Updating..." : "Update SMTP"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {deleteId && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Delete SMTP Config</div>
              <button className="modal-close" onClick={() => setDeleteId(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                Are you sure you want to delete this SMTP configuration?
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                This action cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeleteId(null)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background: "var(--red, #dc2626)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "🗑 Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Template Modal ── */}
      {addTplOpen && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">📧 Create Email Template</div>
              <button
                className="modal-close"
                onClick={() => setAddTplOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Event / Template Name *</label>
                <select
                  value={addTplForm.eventId}
                  onChange={(e) => handleAddTplEventChange(e.target.value)}
                  style={addTplErrors.eventId ? { borderColor: "#dc2626" } : {}}
                >
                  <option value="">— Select Event —</option>
                  {templateEvents.map((ev) => (
                    <option key={ev.eventId} value={ev.eventId}>
                      {ev.eventName}
                    </option>
                  ))}
                </select>
                {addTplErrors.eventId && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addTplErrors.eventId}
                  </span>
                )}
              </div>

              <div className="form-group mb12">
                <label>Subject *</label>
                <input
                  type="text"
                  value={addTplForm.subject}
                  placeholder="e.g. Your invoice for {{month}}"
                  onChange={(e) => {
                    setAddTplForm((p) => ({ ...p, subject: e.target.value }));
                    setAddTplErrors((p) => ({ ...p, subject: "" }));
                  }}
                  style={addTplErrors.subject ? { borderColor: "#dc2626" } : {}}
                />
                {addTplErrors.subject && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addTplErrors.subject}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Message Body *</label>
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    marginBottom: 6,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-xs"
                    style={{ fontWeight: 700, width: 30 }}
                    onClick={() => document.execCommand("bold")}
                  >
                    <b>B</b>
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs"
                    style={{ fontStyle: "italic", width: 30 }}
                    onClick={() => document.execCommand("italic")}
                  >
                    <i>I</i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs"
                    style={{ textDecoration: "underline", width: 30 }}
                    onClick={() => document.execCommand("underline")}
                  >
                    <u>U</u>
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs"
                    onClick={() => document.execCommand("insertUnorderedList")}
                  >
                    • List
                  </button>
                  <span
                    style={{
                      width: 1,
                      height: 20,
                      background: "var(--border-light)",
                      margin: "0 4px",
                    }}
                  />
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        insertVariable(e.target.value, bodyEditorAddRef);
                        e.target.value = "";
                      }
                    }}
                    style={{ fontSize: 11, padding: "4px 6px", width: "auto" }}
                  >
                    <option value="">+ Insert Variable…</option>
                    {templateVariables.map((v) => (
                      <option key={v.variableId} value={v.placeholder}>
                        {v.placeholder}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  ref={bodyEditorAddRef}
                  contentEditable
                  style={{
                    minHeight: 200,
                    border: "0.5px solid var(--border-mid)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: 13,
                    lineHeight: 1.6,
                    background: "#fff",
                    outline: "none",
                    ...(addTplErrors.bodyText
                      ? { borderColor: "#dc2626" }
                      : {}),
                  }}
                />
                {addTplErrors.bodyText && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addTplErrors.bodyText}
                  </span>
                )}
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--text-tertiary)",
                    marginTop: 4,
                  }}
                >
                  Rich text: bold, italic, underline, list. Variables render as
                  plain{" "}
                  <code style={{ fontFamily: "monospace" }}>{"{{name}}"}</code>{" "}
                  in the source.
                </div>
              </div>
              {templateVariables.length > 0 && (
                <div
                  className="info-box"
                  style={{ marginTop: 12, fontSize: 11 }}
                >
                  <strong>Available variables:</strong>{" "}
                  {templateVariables.map((v) => (
                    <code
                      key={v.variableId}
                      style={{
                        fontFamily: "monospace",
                        background: "#fff",
                        padding: "1px 5px",
                        borderRadius: 3,
                        border: "0.5px solid var(--border-light)",
                        marginRight: 3,
                      }}
                    >
                      {v.placeholder}
                    </code>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setAddTplOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddTplSubmit}
                disabled={addTplLoading}
              >
                {addTplLoading ? "Saving..." : "Create Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Template Modal ── */}
      {editTplOpen && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">📧 Edit Email Template</div>
              <button
                className="modal-close"
                onClick={() => setEditTplOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {editTplFetching ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px 0",
                    color: "var(--text-secondary)",
                    fontSize: 13,
                  }}
                >
                  Loading...
                </div>
              ) : (
                <>
                  <div className="form-group mb12">
                    <label>Event *</label>
                    <select
                      value={editTplForm.eventId}
                      onChange={(e) => handleEditTplEventChange(e.target.value)}
                      style={
                        editTplErrors.eventId ? { borderColor: "#dc2626" } : {}
                      }
                    >
                      <option value="">— Select Event —</option>
                      {templateEvents.map((ev) => (
                        <option key={ev.eventId} value={ev.eventId}>
                          {ev.eventName}
                        </option>
                      ))}
                    </select>
                    {editTplErrors.eventId && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editTplErrors.eventId}
                      </span>
                    )}
                  </div>

                  <div className="form-group mb12">
                    <label>Subject *</label>
                    <input
                      type="text"
                      value={editTplForm.subject}
                      onChange={(e) => {
                        setEditTplForm((p) => ({
                          ...p,
                          subject: e.target.value,
                        }));
                        setEditTplErrors((p) => ({ ...p, subject: "" }));
                      }}
                      style={
                        editTplErrors.subject ? { borderColor: "#dc2626" } : {}
                      }
                    />
                    {editTplErrors.subject && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editTplErrors.subject}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Message Body *</label>
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        marginBottom: 6,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-xs"
                        style={{ fontWeight: 700, width: 30 }}
                        onClick={() => document.execCommand("bold")}
                      >
                        <b>B</b>
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs"
                        style={{ fontStyle: "italic", width: 30 }}
                        onClick={() => document.execCommand("italic")}
                      >
                        <i>I</i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs"
                        style={{ textDecoration: "underline", width: 30 }}
                        onClick={() => document.execCommand("underline")}
                      >
                        <u>U</u>
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs"
                        onClick={() =>
                          document.execCommand("insertUnorderedList")
                        }
                      >
                        • List
                      </button>
                      <span
                        style={{
                          width: 1,
                          height: 20,
                          background: "var(--border-light)",
                          margin: "0 4px",
                        }}
                      />
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            insertVariable(e.target.value, bodyEditorEditRef);
                            e.target.value = "";
                          }
                        }}
                        style={{
                          fontSize: 11,
                          padding: "4px 6px",
                          width: "auto",
                        }}
                      >
                        <option value="">+ Insert Variable…</option>
                        {templateVariables.map((v) => (
                          <option key={v.variableId} value={v.placeholder}>
                            {v.placeholder}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      ref={bodyEditorEditRef}
                      contentEditable
                      style={{
                        minHeight: 200,
                        border: "0.5px solid var(--border-mid)",
                        borderRadius: "var(--radius-md)",
                        padding: "10px 12px",
                        fontSize: 13,
                        lineHeight: 1.6,
                        background: "#fff",
                        outline: "none",
                        ...(editTplErrors.bodyText
                          ? { borderColor: "#dc2626" }
                          : {}),
                      }}
                    />
                    {editTplErrors.bodyText && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editTplErrors.bodyText}
                      </span>
                    )}
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--text-tertiary)",
                        marginTop: 4,
                      }}
                    >
                      Rich text: bold, italic, underline, list.
                    </div>
                  </div>
                  {templateVariables.length > 0 && (
                    <div
                      className="info-box"
                      style={{ marginTop: 12, fontSize: 11 }}
                    >
                      <strong>Available variables:</strong>{" "}
                      {templateVariables.map((v) => (
                        <code
                          key={v.variableId}
                          style={{
                            fontFamily: "monospace",
                            background: "#fff",
                            padding: "1px 5px",
                            borderRadius: 3,
                            border: "0.5px solid var(--border-light)",
                            marginRight: 3,
                          }}
                        >
                          {v.placeholder}
                        </code>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {!editTplFetching && (
              <div className="modal-footer">
                <button className="btn" onClick={() => setEditTplOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleEditTplSubmit}
                  disabled={editTplLoading}
                >
                  {editTplLoading ? "Updating..." : "Update Template"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Template Modal ── */}
      {deleteTemplateId && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Delete Email Template</div>
              <button
                className="modal-close"
                onClick={() => setDeleteTemplateId(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                Are you sure you want to delete this email template?
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                This action cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeleteTemplateId(null)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background: "var(--red, #dc2626)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleDeleteTemplate}
                disabled={deleteTemplateLoading}
              >
                {deleteTemplateLoading ? "Deleting..." : "🗑 Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewTpl && (
        <div className="modal-overlay open" onClick={() => setPreviewTpl(null)}>
          <div
            className="modal"
            style={{ width: 600, maxWidth: "98vw" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header"
              style={{
                background: "#4f46e5",
                borderRadius: "var(--radius-md) var(--radius-md) 0 0",
              }}
            >
              <div className="modal-title" style={{ color: "#fff" }}>
                📧 Email Preview — {previewTpl.templateName}
              </div>
              <button
                className="modal-close"
                style={{ color: "#fff" }}
                onClick={() => setPreviewTpl(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              <div style={{ background: "#f3f4f6", padding: "12px 20px" }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 4,
                  }}
                >
                  Subject
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {previewTpl.subject}
                </div>
              </div>
              <div
                style={{ padding: "20px", background: "#fff", minHeight: 200 }}
              >
                {previewTpl.isHtml ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: previewTpl.bodyText }}
                  />
                ) : (
                  <pre
                    style={{
                      fontFamily: "inherit",
                      fontSize: 13,
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {previewTpl.bodyText}
                  </pre>
                )}
              </div>
              <div
                style={{
                  padding: "10px 20px",
                  background: "#f9fafb",
                  borderTop: "0.5px solid var(--border-light)",
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  textAlign: "center",
                }}
              >
                Preview uses sample values for variables. Real messages
                substitute live tenant/owner data.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setPreviewTpl(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
